import type { Observable } from 'rxjs';
import { map, of, Subject } from 'rxjs';

// Code adapted from https://developer.chrome.com/docs/web-platform/page-lifecycle-api and https://github.com/GoogleChromeLabs/page-lifecycle/blob/master/src/Lifecycle.mjs

export type LifecycleState = 'hidden' | 'active' | 'passive' | 'frozen' | 'terminated' | 'not available (no browser)';

const isBrowser = typeof window !== 'undefined';
let state: LifecycleState = isBrowser ? getState() : 'not available (no browser)';
let _lifecycleApiIsSupported = false;
let _wasFrozen = false;
let _isOrWasPersisted: boolean;
const event$ = new Subject<void>();

if (isBrowser) {
  setupUpdatingState();
  _lifecycleApiIsSupported = 'wasDiscarded' in document;
}

/**
 * Current state as documented in [Lifecycle API Docs](https://developer.chrome.com/docs/web-platform/page-lifecycle-api)
 *
 * Note: Not all browsers support (reporting) all states. Be sure you understand everything from the link above before using this for anything critical. Also, the result makes no sense outside of the browser (e.g. NodeJS)
 */
export function lifecycleState(): LifecycleState {
  return state;
}

/**
 * Emits state changes as documented in [Lifecycle API Docs](https://developer.chrome.com/docs/web-platform/page-lifecycle-api)
 *
 * Note: Not all browsers support (reporting) all states. Be sure you understand everything from the link above before using this for anything critical. Also, the result makes no sense outside of the browser (e.g. NodeJS)
 */
export function lifecycleState$(): Observable<LifecycleState> {
  if (!isBrowser) {
    return of(state);
  }

  return event$.pipe(map(() => state));
}

/**
 * Whether the page has been frozen before (similar to document.wasDiscarded). [Lifecycle API Docs](https://developer.chrome.com/docs/web-platform/page-lifecycle-api)
 */
export function wasFrozen(): boolean {
  return _wasFrozen;
}

/**
 * Whether a webpage is loading from - or has been added to - a cache (back/forward cache) [as reported by persisted prop on pageshow and pagehide events](https://developer.mozilla.org/en-US/docs/Web/API/PageTransitionEvent/persisted)
 *
 * Note: If undefined (and not false) is returned, that means we don't know yet (It doesn't mean it wasn't in a cache)
 */
export function isOrWasPersisted(): boolean | undefined {
  return _isOrWasPersisted;
}

/**
 * Whether the [additional Lifecycle API stuff as introduced by Google](https://developer.chrome.com/docs/web-platform/page-lifecycle-api) is supported (by checking if document.wasDiscarded exists).
 *
 * Note: Lifecycle states still make sense without that, but are a bit more limited and just differ a bit (see docs for details)
 */
export function lifecycleApiIsSupported(): boolean {
  return _lifecycleApiIsSupported;
}

function getState(): LifecycleState {
  if (document.visibilityState === 'hidden') {
    return 'hidden';
  }
  if (document.hasFocus()) {
    return 'active';
  }
  return 'passive';
}

function updateState(newState: LifecycleState): void {
  const prevState = state;
  state = newState;
  if (state !== prevState) {
    event$.next();

    if (state === 'frozen') {
      _wasFrozen = true;
    }
  }
}

function setupUpdatingState(): void {
  const opts = { capture: true };

  // These lifecycle events can all use the same listener to observe state changes (they call the `getState()` function to determine the next state).
  ['focus', 'blur', 'visibilitychange', 'resume', 'freeze', 'pageshow', 'pagehide'].forEach((type) => {
    window.addEventListener(type, (event) => handleEvent(event), opts);
  });
}

function handleEvent(event: Event | PageTransitionEvent): void {
  if (event instanceof PageTransitionEvent) {
    _isOrWasPersisted = event.persisted;
  }

  switch (event.type) {
    case 'focus':
    case 'resume':
    case 'pageshow':
      updateState(getState());
      break;
    case 'blur':
      // The `blur` event can fire while the page is being unloaded, so we only need to update the state if the current state is "active".
      if (state === 'active') {
        updateState(getState());
      }
      break;
    case 'visibilitychange':
      // Need to exclude these because of the way pagehide event is used to indicate frozen and terminated states
      if (state !== 'frozen' && state !== 'terminated') {
        updateState(getState());
      }
      break;
    case 'freeze':
      updateState('frozen');
      break;
    case 'pagehide':
      // If the event's persisted property is `true` the page is about to enter the (back/forward) cache, which is also in the frozen state.
      // If the event's persisted property is not `true` the page is about to be unloaded.
      updateState((event as PageTransitionEvent).persisted ? 'frozen' : 'terminated');
      break;
    default:
      console.error('Unhandled event in lifecycle helper. This should not happen. Have a look!');
  }
}
